// src/components/resources/ResourceCard.tsx

import { logClientError } from '~/lib/logger-client'
import type { BaseResource, ResourceStats } from '~/lib/resources/types/shared/base'
import type { GlossaryCategory } from '~/lib/resources/types/shared/enums'
import { getGlossaryCategoryShortLabel, getResourceTypeLabel } from '~/lib/resources/types/shared/enums'
import { resourceHref } from '~/lib/resources/resource-path'
import { isEbook } from '~/lib/resources/types/ebooks'
import { getEbookCoverTheme } from '~/lib/resources/data/ebooks/themes'
import { EbookCover } from '~/components/resources/ebooks/EbookCover'
import { isGlossaryTerm, isToolType } from '~/lib/resources/types/shared/type-guards'
import { TemplateCategoryDisplayNames, type TemplateCategoryType } from '~/lib/resources/types/templates/base'
import Image from 'next/image'
import Link from 'next/link'
import { cn } from '~/lib/utils'
import RiTimeLine from '~/icons/remix/generated/RiTimeLine'
import RiDownloadLine from '~/icons/remix/generated/RiDownloadLine'
import RiShareLine from '~/icons/remix/generated/RiShareLine'
import { formatDate } from '~/lib/utils/date'
import { formatNumber } from '~/lib/utils/formatters'

function getGlossaryCategoryDisplayName(category: GlossaryCategory): string {
  return getGlossaryCategoryShortLabel(category)
}

function getCategoryDisplayName(resource: BaseResource): string {
  if (resource.resourceType === 'template' && resource.category) {
    return TemplateCategoryDisplayNames[resource.category as TemplateCategoryType] ?? resource.category
  }
  if (resource.resourceType === 'ebook' && resource.category) {
    return getEbookCoverTheme(resource.category).label
  }
  if (isGlossaryTerm(resource) && resource.category) {
    return getGlossaryCategoryDisplayName(resource.category as GlossaryCategory)
  }
  // Delegate tool-type labels to the shared formatter so the badge strings
  // have a single source of truth (see RESOURCE_TYPE_LABELS); category-specific
  // branches above (glossary/template/ebook) stay as-is.
  if (resource.resourceType === 'tool' && 'toolType' in resource && isToolType(resource.toolType)) {
    return getResourceTypeLabel(resource.toolType)
  }
  if (resource.category) {
    return resource.category.charAt(0).toUpperCase() + resource.category.slice(1)
  }
  return ''
}

type ResourceCardVariant = 'default' | 'featured' | 'compact' | 'minimal' | 'grid' | 'list'
type ImageAspectRatio = '16/9' | '4/3' | '1/1'

interface ResourceCardFeatures {
  showImage?: boolean
  showCategory?: boolean
  showDescription?: boolean
  showMetadata?: boolean
  showDifficulty?: boolean
  showLastUpdated?: boolean
  showStats?: {
    estimatedTime?: boolean
    downloads?: boolean
    shares?: boolean
  }
}

interface ResourceCardTheme {
  variant?: ResourceCardVariant
  imageAspectRatio?: ImageAspectRatio
  hoverAnimation?: boolean
}

interface ResourceCardProps {
  resource: BaseResource
  theme?: ResourceCardTheme
  features?: ResourceCardFeatures
  category?: 'template' | 'guide' | 'ebook' | 'tool' | 'calculator' | 'glossary-term'
  type?: 'default' | 'featured' | 'compact' // For backwards compatibility
}

const PRESET_VARIANTS = {
  default: {
    theme: {
      variant: 'default' as const,
      imageAspectRatio: '16/9' as const,
      hoverAnimation: true
    },
    features: {
      showImage: true,
      showCategory: true,
      showDescription: true,
      showMetadata: true,
      showDifficulty: true,
      showLastUpdated: false,
      showStats: { estimatedTime: true, downloads: false, shares: false }
    }
  },
  compact: {
    theme: {
      variant: 'compact' as const,
      imageAspectRatio: '4/3' as const,
      hoverAnimation: true
    },
    features: {
      showImage: true,
      showCategory: true,
      showDescription: false,
      showMetadata: true,
      showDifficulty: false,
      showLastUpdated: false,
      showStats: { estimatedTime: true, downloads: false, shares: false }
    }
  },
  minimal: {
    theme: {
      variant: 'minimal' as const,
      hoverAnimation: false
    },
    features: {
      showImage: false,
      showCategory: true,
      showDescription: true,
      showMetadata: false,
      showDifficulty: false,
      showLastUpdated: false,
      showStats: { estimatedTime: false, downloads: false, shares: false }
    }
  }
} as const

function getImageSizes(variant: ResourceCardVariant): string {
  switch (variant) {
    case 'list':
      // In list view, image takes 1/3 of the card width
      return '(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 25vw'
    case 'compact':
      // Compact cards are typically in grids
      return '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
    case 'minimal':
      // Minimal variant doesn't show images by default, but just in case
      return '(max-width: 768px) 100vw, 50vw'
    case 'grid':
      // Grid layout with multiple columns
      return '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
    case 'featured':
      // Featured cards are typically larger
      return '(max-width: 768px) 100vw, (max-width: 1200px) 75vw, 50vw'
    case 'default':
      // Default variant - responsive card layout
      return '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
  }
}

export function ResourceCard({ 
  resource,
  theme: customTheme,
  features: customFeatures,
  type // Backwards compatibility prop
}: ResourceCardProps): JSX.Element {
  // Handle backwards compatibility by converting 'type' to theme/features
  const presetKey = type && type in PRESET_VARIANTS ? type : 'default'
  const selectedVariant = PRESET_VARIANTS[presetKey as keyof typeof PRESET_VARIANTS]
  
  // Merge custom theme/features with preset, prioritizing custom settings
  const theme: ResourceCardTheme = { ...selectedVariant.theme, ...customTheme }
  const features: ResourceCardFeatures = { 
    ...selectedVariant.features, 
    ...customFeatures,
    showStats: {
      ...selectedVariant.features.showStats,
      ...customFeatures?.showStats
    }
  }

  // Initialize stats with defaults
  const defaultStats: Required<ResourceStats> = {
    views: 0,
    shares: 0,
    downloads: 0,
    completions: 0,
    averageRating: 0,
    totalRatings: 0,
    lastUpdated: ''
  }

  // Merge with resource stats if they exist
  const stats = resource.stats ? { ...defaultStats, ...resource.stats } : defaultStats

  // Share functionality
  const handleShare = async (): Promise<void> => {
    try {
      await navigator.share({
        title: resource.title,
        text: resource.description,
        url: window.location.origin + resourceHref(resource)
      })
      // TODO: Update share count via API
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        logClientError(error, { component: 'ResourceCard', context: 'Error sharing' })
      }
    }
  }

  const categoryLabel = getCategoryDisplayName(resource)

  return (
    <Link href={resourceHref(resource)}>
      <article
        className={cn(
          // `transition-[transform,box-shadow]` keeps the GPU on
          // compositor-only properties; Motion docs flag transform/opacity as
          // the only widely-accelerated styles. `motion-safe:` ensures we
          // honor the user's reduced-motion preference (parity with what
          // Motion's own `useReducedMotion` did before this refactor).
          "group relative flex flex-col overflow-hidden rounded-2xl bg-white shadow-md ring-1 ring-gray-200 transition-[transform,box-shadow] duration-300",
          "hover:shadow-xl dark:bg-gray-800/50 dark:ring-gray-800 dark:hover:bg-gray-800",
          theme.hoverAnimation && "motion-safe:hover:-translate-y-1",
          theme.variant === 'list' && 'flex-row',
          theme.variant === 'minimal' && 'shadow-none ring-0'
        )}
      >
        {/* Image */}
        {features.showImage && (
          <div className={cn(
            "relative overflow-hidden",
            theme.variant === 'list' ? 'w-1/3' : '',
            isEbook(resource) ? 'aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-900 dark:to-gray-950' : '',
            !isEbook(resource) && {
              'aspect-[16/9]': theme.imageAspectRatio === '16/9',
              'aspect-[4/3]': theme.imageAspectRatio === '4/3',
              'aspect-[1/1]': theme.imageAspectRatio === '1/1'
            }
          )}>
            {isEbook(resource) ? (
              <div className="flex h-full items-center justify-center p-6">
                <EbookCover
                  title={resource.title}
                  category={resource.category}
                  author={resource.author}
                  pageCount={resource.pageCount}
                  size="md"
                  elevated
                />
              </div>
            ) : (
              <>
                <Image
                  src={resource.images?.thumbnail?.url ?? resource.images?.hero?.url ?? '/images/resources/adsights-digital-marketing-resource-center-home-hero-image.webp'}
                  alt={resource.title}
                  fill
                  sizes={getImageSizes(theme.variant ?? 'default')}
                  className="object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-transparent" />
              </>
            )}
            {features.showCategory && resource.category && (
              <div className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-sm font-medium text-gray-900 backdrop-blur dark:bg-gray-900/90 dark:text-white">
                {categoryLabel}
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className={cn(
          "flex flex-col p-4 sm:p-6",
          theme.variant === 'minimal' && 'p-4',
          theme.variant === 'list' && 'w-2/3'
        )}>
          {!features.showImage && features.showCategory && categoryLabel && (
            <span className="mb-2 inline-flex w-fit items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
              {categoryLabel}
            </span>
          )}
          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600 dark:text-white dark:group-hover:text-indigo-400">
            {resource.title ?? resource.canonicalTitle}
          </h3>
          {features.showDescription && (
            <p className="mt-2 text-sm text-gray-600 line-clamp-2 dark:text-gray-400">
              {isGlossaryTerm(resource) ? resource.shortDescription : resource.description}
            </p>
          )}

          {/* Metadata */}
          {features.showMetadata && (
            <div className="mt-4 flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
              {features.showStats?.estimatedTime && 'estimatedTime' in resource && resource.estimatedTime && (
                <div className="flex items-center gap-1">
                  <RiTimeLine className="h-4 w-4" />
                  <span>{resource.estimatedTime}</span>
                </div>
              )}
              {isEbook(resource) && resource.pageCount && (
                <div className="flex items-center gap-1">
                  <RiDownloadLine className="h-4 w-4" />
                  <span>{resource.pageCount} pages</span>
                </div>
              )}
              {features.showStats?.downloads && stats.downloads > 0 && (
                <div className="flex items-center gap-1">
                  <RiDownloadLine className="h-4 w-4" />
                  <span>{formatNumber(stats.downloads)}</span>
                </div>
              )}
              {features.showStats?.shares && (
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    void handleShare()
                  }}
                  className="flex items-center gap-1 hover:text-indigo-600 dark:hover:text-indigo-400"
                >
                  <RiShareLine className="h-4 w-4" />
                  <span>{stats.shares > 0 ? formatNumber(stats.shares) : 'Share'}</span>
                </button>
              )}
            </div>
          )}

          {/* Difficulty Badge */}
          {features.showDifficulty && 'difficulty' in resource && resource.difficulty && (
            <div className="mt-4">
              <span className={cn(
                "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium",
                {
                  'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400': resource.difficulty === 'beginner',
                  'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400': resource.difficulty === 'intermediate',
                  'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400': resource.difficulty === 'advanced'
                }
              )}>
                {resource.difficulty.charAt(0).toUpperCase() + resource.difficulty.slice(1)}
              </span>
            </div>
          )}
        </div>

        {/* Last Updated */}
        {features.showLastUpdated && 'lastUpdated' in resource && resource.lastUpdated && (
          <div className="border-t border-gray-200 px-6 py-3 text-xs text-gray-600 dark:border-gray-700 dark:text-gray-400">
            Last updated {formatDate(resource.lastUpdated)}
          </div>
        )}
      </article>
    </Link>
  )
}